import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { Skeleton } from "@garden/ui";

import { registerSellerPortalI18n, SELLER_PORTAL_NS } from "@/features/seller-portal/i18n";
import { useSellerProfile } from "@/features/seller-portal/hooks";
import { ApiError } from "@/lib/api-client";
import { ShopProfileSection } from "@/features/seller-portal/sections/ShopProfileSection";
import { ContactChannelsSection } from "@/features/seller-portal/sections/ContactChannelsSection";
import { LocationSection } from "@/features/seller-portal/sections/LocationSection";
import { StorefrontDetailsSection } from "@/features/seller-portal/sections/StorefrontDetailsSection";
import { AvailabilitySection } from "@/features/seller-portal/sections/AvailabilitySection";
import { AccountSecuritySection } from "@/features/seller-portal/sections/AccountSecuritySection";

export function SellerSettingsPage(): ReactElement {
  registerSellerPortalI18n();
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const { data, isLoading, error } = useSellerProfile();

  if (error instanceof ApiError && error.code === "forbidden.not_a_seller") {
    return <Navigate to="/account" replace />;
  }

  if (isLoading || !data) {
    return (
      <div data-testid="settings-loading">
        <Skeleton h={160} />
      </div>
    );
  }

  const readOnly = data.seller.verification_status === "suspended";

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <h1 className="text-xl font-semibold text-[var(--g-ink)]">{t("settings.title")}</h1>
      {readOnly ? (
        <div
          data-testid="settings-suspended-banner"
          role="alert"
          className="rounded-[var(--g-radius)] bg-[var(--g-accent-soft)] px-4 py-3 text-sm text-[var(--g-ink)]"
        >
          {t("suspended")}
        </div>
      ) : null}
      <ShopProfileSection profile={data} readOnly={readOnly} />
      <ContactChannelsSection profile={data} readOnly={readOnly} />
      <LocationSection profile={data} readOnly={readOnly} />
      <StorefrontDetailsSection profile={data} readOnly={readOnly} />
      <AvailabilitySection profile={data} readOnly={readOnly} />
      <AccountSecuritySection profile={data} />
    </div>
  );
}
